import React, {MutableRefObject, useEffect, useRef, useState} from 'react';

import classNames from 'classnames';
import { format as dFormat, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale';
import {
    addToRange,
    DayPicker,
    DayModifiers,
    DateRange,
    DayPickerRangeProps,
    DayPickerSingleProps,
    isDayPickerRange,
    isDateRange,
    ModifiersClassNames,
    StyledElement,
} from 'react-day-picker';

import { Dropdown, IDropdownProps } from '../../dropdown';
import { isMobileDevice } from '../../helpers';
import { Grid, GridColumn } from '../../grid';
import { ITextFieldProps, TextField } from '../textfield';

export interface IDatepickerBaseProps extends Omit<IDropdownProps, 'children'> {
    /**
     * Format of selected value.
     * For more info go to: https://date-fns.org/v2.30.0/docs/format
     */
    format: string;
    onOpen?: () => void;
    /**
     * Type of datepicker.
     */
    type?: 'single' | 'range';
}

export interface IDatepickerSingleProps extends IDatepickerBaseProps, Omit<DayPickerSingleProps, 'mode'> {
    /**
     * Value if component is controlled.
     */
    value?: Date;
    /**
     * Default value (used when component is not controlled).
     */
    defaultValue?: Date;
    /**
     * Date change callback.
     */
    onDateChange?: (date: Date | undefined) => void;
    /**
     * Datepicker field element.
     */
    field: IDatepickerFieldProps;
    /**
     * Type of datepicker.
     */
    type?: 'single';
}

export interface IDatepickerRangeProps extends IDatepickerBaseProps, Omit<DayPickerRangeProps, 'mode'> {
    /**
     * Value if component is controlled.
     */
    value?: DateRange;
    /**
     * Default value (used when component is not controlled).
     */
    defaultValue?: DateRange;
    /**
     * Date change callback.
     */
    onDateChange?: (date: DateRange | undefined) => void;
    /**
     * Datepicker field elements.
     */
    field: [IDatepickerFieldProps, IDatepickerFieldProps];
    /**
     * Type of datepicker.
     */
    type: 'range';
}

export type IDatepickerProps = IDatepickerSingleProps | IDatepickerRangeProps;
export type IDatepickerDate = Date | undefined | null;
export type IDatepickerFieldProps = ITextFieldProps;

const DatepickerToggle = (props: IDatepickerFieldProps): JSX.Element => {
    const className: string = classNames(
        'datepicker-field',
        props.className,
    );

    return (
        <TextField
            {...props}
            attributes={{
                autoComplete: 'off',
                readOnly: !isMobileDevice(),
            }}
            className={className}
            isStatic={props.isStatic ? props.isStatic : isMobileDevice()}
        />
    );
};

const Datepicker = (props: IDatepickerProps): JSX.Element => {
    const {
        defaultValue,
        format = 'dd-MM-yyyy',
        locale = enUS,
        onDateChange,
        showOutsideDays = true,
        type,
        value,
        weekStartsOn = 1,
        ...rest
    } = props;
    const className: string = classNames(
        'datepicker',
        {
            [`datepicker--type-${type}`]: !!type,
        },
    );
    const dropdownDefaultProps: Partial<IDropdownProps> = {
        ...props,
        className,
        offset: [0, 17],
        triggerType: 'click',
    };

    const fromField: MutableRefObject<null | HTMLTextAreaElement | HTMLInputElement> = useRef(null);
    const toField: MutableRefObject<null | HTMLTextAreaElement | HTMLInputElement> = useRef(null);
    const [selectedDay, setSelectedDay] = useState<Date | DateRange | undefined>(defaultValue);
    const [startOpen, setStartOpen] = useState<boolean>(false);
    const [endOpen, setEndOpen] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState(false);
    const isControlled: boolean = typeof value !== 'undefined' || typeof onDateChange !== 'undefined';

    const getValue: (valFormat: string, valType?: 'to' | 'from') => string | undefined = (valFormat: string, valType?: 'to' | 'from') => {
        if (isControlled) {
            if (type === 'range' && valType) {
                if (valType === 'to') {
                    return typeof value !== 'undefined' && (value as DateRange).to !== undefined ? getToDateString(value) : '';
                } else {
                    return typeof value !== 'undefined' && (value as DateRange).from !== undefined ? getFromDateString(value) : '';
                }
            } else {
                return typeof value !== 'undefined' ? dFormat(value as Date, valFormat) : undefined;
            }
        } else if (selectedDay) {
            if (type === 'range' && valType) {
                if (valType === 'to') {
                    return getToDateString(selectedDay);
                } else {
                    return getFromDateString(selectedDay);
                }
            } else {
                return dFormat(selectedDay as Date, valFormat);
            }
        }

        return undefined;
    };

    React.useEffect(() => {
        setIsMounted(true);

        return (): void => {
            setIsMounted(false);
        };
    }, []);

    useEffect(() => {
        if (endOpen) {
            setStartOpen(false);
            if (props?.onOpen) {
                props.onOpen();
            }

            if ((!selectedDay || (selectedDay as DateRange).from === undefined) && fromField.current) {
                fromField.current.focus();
                setStartOpen(true);
                setEndOpen(false);
            }
        }

    }, [endOpen]);

    useEffect(() => {
        if (startOpen) {
            setEndOpen(false);
            if (props?.onOpen) {
                props.onOpen();
            }
        }

    }, [startOpen]);

    const isFocused = (el: HTMLElement): boolean => document.activeElement === el;

    const reset = (): void => {
        setSelectedDay({
            from: undefined,
            to: undefined,
        });
        setStartOpen(true);
        setEndOpen(false);
    };

    const handleDayClick = (fieldType: 'from' | 'to', date: Date, modifiers: DayModifiers): void => {
        if (!modifiers || (!modifiers.disabled)) {
            if (type === 'range') {
                let from;
                let to;

                if (selectedDay) {
                    if ((selectedDay as DateRange).from) {
                        from = (selectedDay as DateRange).from;
                    }

                    if ((selectedDay as DateRange).to) {
                        to = (selectedDay as DateRange).to;
                    }
                }

                if (from && to && date >= from && date <= to) {
                    reset();
                } else if (fieldType === 'from') {
                    setSelectedDay({
                        from: date,
                        to: (selectedDay as DateRange)?.to,
                    });

                    if (startOpen && toField.current) {
                        toField.current.focus();
                        setStartOpen(false);
                    } else if (endOpen && fromField.current) {
                        fromField.current.focus();
                        setEndOpen(false);
                    }
                } else if (fieldType === 'to') {
                    setSelectedDay({
                        from: (selectedDay as DateRange).from ? (selectedDay as DateRange).from : undefined,
                        to: date,
                    });
                }

                const range: DateRange | undefined = addToRange(date, selectedDay as DateRange);

                if (onDateChange) {
                    onDateChange(range);
                }
            } else {
                setSelectedDay(date);

                if (onDateChange) {
                    onDateChange(date);
                }
            }
        }
    };

    const modifierClasses: ModifiersClassNames = {
        disabled: 'is-disabled',
        hidden: 'is-hidden',
        outside: 'is-outside',
        range_start: 'datepicker__range--start',
        range_middle: 'datepicker__range--mid',
        range_end: 'datepicker__range--end',
        selected: 'is-selected',
        today: 'is-today',
    };

    const classes: Partial<StyledElement<string>> = {
        button: 'datepicker__button',
        button_reset: 'datepicker__button--reset',
        caption: 'datepicker__caption',
        caption_between: 'datepicker__caption-between',
        caption_dropdowns: 'datepicker__caption-dropdowns',
        caption_end: 'datepicker__caption-end',
        caption_label: 'datepicker__caption-label',
        caption_start: 'datepicker__caption-start',
        cell: 'datepicker__cell',
        day: 'datepicker__day',
        day_disabled: 'datepicker__day--disabled',
        day_hidden: 'datepicker__day--hidden',
        day_outside: 'datepicker__day--outside',
        day_range_end: 'datepicker__day-range--end',
        day_range_middle: 'datepicker__day-range--mid',
        day_range_start: 'datepicker__day-range--start',
        day_selected: 'datepicker__day--selected',
        day_today: 'datepicker__day--today',
        dropdown: 'datepicker__dropdown',
        dropdown_icon: 'datepicker__dropdown-icon',
        dropdown_month: 'datepicker__dropdown-month',
        dropdown_year: 'datepicker__dropdown-year',
        head: 'datepicker__head',
        head_cell: 'datepicker__head-cell',
        head_row: 'datepicker__head-row',
        month: 'datepicker__month',
        months: 'datepicker__months',
        multiple_months: 'datepicker__months--multiple',
        nav: 'datepicker__nav',
        nav_button: 'datepicker__nav-button',
        nav_button_next: 'datepicker__nav-button--next',
        nav_button_previous: 'datepicker__nav-button--prev',
        nav_icon: 'datepicker__nav-icon',
        root: 'datepicker__root',
        row: 'datepicker__row',
        table: 'datepicker__table',
        tbody: 'datepicker__table-body',
        tfoot: 'datepicker__table-footer',
        vhidden: 'visually-hidden',
        weeknumber: 'datepicker__week-nr',
        with_weeknumber: 'datepicker--with-week-nr',
    };

    const renderDayPicker = (fieldType: 'from' | 'to'): JSX.Element => {
        const dayPickerValue: DateRange | Date | undefined = isControlled ? props.value : selectedDay;

        const dayPickerSingleProps: DayPickerSingleProps = {
            ...rest as IDatepickerSingleProps,
            mode: 'single',
            selected: !isDateRange(dayPickerValue) ? dayPickerValue as Date : undefined,
        };

        const dayPickerRangeProps: DayPickerRangeProps = {
            ...rest as IDatepickerRangeProps,
            mode: 'range',
            selected: isDateRange(dayPickerValue) ? dayPickerValue as DateRange : undefined,
        };

        return (
            <DayPicker
                {...(isDayPickerRange(props) ? dayPickerRangeProps : dayPickerSingleProps)}
                className={'datepicker__content'}
                classNames={classes}
                locale={locale}
                modifiersClassNames={modifierClasses}
                onDayClick={(date, dayModifiers): void => handleDayClick(fieldType, date, dayModifiers)}
                showOutsideDays={showOutsideDays}
                weekStartsOn={weekStartsOn}
            />
        );
    };

    const renderSingleField: (datePickerProps: IDatepickerSingleProps) => JSX.Element = (datePickerProps: IDatepickerSingleProps) => {
        const trigger: IDatepickerFieldProps = datePickerProps.field;

        if (isMobileDevice()) {
            return (
                <DatepickerToggle
                    {...trigger}
                    onChange={(newValue): void => {
                        const nextSelectedDay = new Date(newValue);

                        if (isValid(nextSelectedDay)) {
                            setSelectedDay(nextSelectedDay);
                        }

                        if (datePickerProps.onDateChange) {
                            datePickerProps.onDateChange(selectedDay as Date);
                        }
                    }}
                    type="date"
                    value={getValue('yyyy-MM-dd')}
                />
            );
        } else {
            return (
                <Dropdown
                    {...dropdownDefaultProps}
                    trigger={{
                        children: (
                            <DatepickerToggle
                                {...trigger}
                                value={getValue(format)}
                            />
                        ),
                    }}
                >
                    {renderDayPicker('from')}
                </Dropdown>
            );
        }
    };

    const getFromDateString: (val: Date | DateRange | undefined) => string | undefined = (val: Date | DateRange | undefined) => {
        if (val && (val as DateRange).from !== null && isValid((val as DateRange).from)) {
            if (isMobileDevice()) {
                return dFormat((val as DateRange).from as Date, 'yyyy-MM-dd');
            } else {
                return dFormat((val as DateRange).from as Date, format);
            }
        }

        return undefined;
    };

    const getToDateString: (val: Date | DateRange | undefined) => string | undefined = (val: Date | DateRange | undefined) => {
        if (val && (val as DateRange).to !== null && isValid((val as DateRange).to)) {
            if (isMobileDevice()) {
                return dFormat((val as DateRange).to as Date, 'yyyy-MM-dd');
            } else {
                return dFormat((val as DateRange).to as Date, format);
            }
        }

        return undefined;
    };

    const renderRangeFields: (datePickerProps: IDatepickerRangeProps) => JSX.Element = (datePickerProps: IDatepickerRangeProps) => {
        const [fromTrigger, toTrigger] = datePickerProps.field;

        return <Grid noWrap={['md']} gutter="small">
            {isMobileDevice() &&
                <>
                    <GridColumn width={['max-sm']}>
                        <DatepickerToggle
                            {...fromTrigger}
                            onChange={(newValue): void => {
                                const nextFrom = new Date(newValue);
                                const nextSelectedDay: DateRange = {
                                    from: isValid(nextFrom) ? nextFrom : undefined,
                                    to: selectedDay && (selectedDay as DateRange).to ? (selectedDay as DateRange).to : undefined,
                                };

                                setSelectedDay(nextSelectedDay);

                                if (datePickerProps.onDateChange) {
                                    datePickerProps.onDateChange(nextSelectedDay);
                                }
                            }}
                            type="date"
                            value={getValue(format, 'from')}
                        />
                    </GridColumn>
                    <GridColumn width={['max-sm']}>
                        <DatepickerToggle
                            {...toTrigger}
                            onChange={(newValue): void => {
                                const nextTo = new Date(newValue);
                                const nextSelectedDay: DateRange = {
                                    from: selectedDay && (selectedDay as DateRange).from ? (selectedDay as DateRange).from : undefined,
                                    to: isValid(nextTo) ? nextTo : undefined,
                                };

                                setSelectedDay(nextSelectedDay);

                                if (datePickerProps.onDateChange) {
                                    datePickerProps.onDateChange(nextSelectedDay);
                                }
                            }}
                            type="date"
                            value={getValue(format, 'to')}
                        />
                    </GridColumn>
                </>
            }
            {!isMobileDevice() &&
                <>
                    <GridColumn width={['max-sm']}>
                        <Dropdown
                            {...dropdownDefaultProps}
                            isOpen={startOpen}
                            onVisibleChange={(visible): void => {
                                if (fromField.current && !isFocused(fromField.current)) {
                                    setStartOpen(visible);
                                }
                            }}
                            trigger={{
                                children: (
                                    <DatepickerToggle
                                        {...fromTrigger}
                                        elementRef={(el: HTMLInputElement | HTMLTextAreaElement | null): void => {
                                            if (el && fromField.current !== el) {
                                                fromField.current = el;
                                            }
                                        }}
                                        value={getValue(format, 'from')}
                                        onFocus={(): void => setStartOpen(true)}
                                    />
                                ),
                            }}
                        >
                            {renderDayPicker('from')}
                        </Dropdown>
                    </GridColumn>
                    <GridColumn width={['max-sm']}>
                        <Dropdown
                            {...dropdownDefaultProps}
                            isOpen={endOpen}
                            onVisibleChange={(visible): void => {
                                if (toField.current && !isFocused(toField.current)) {
                                    setEndOpen(visible);
                                }
                            }}
                            trigger={{
                                children: (
                                    <DatepickerToggle
                                        {...toTrigger}
                                        elementRef={(el: HTMLInputElement | HTMLTextAreaElement | null): void => {
                                            if (el && toField.current !== el) {
                                                toField.current = el;
                                            }
                                        }}
                                        value={getValue(format, 'to')}
                                        onFocus={(): void => setEndOpen(true)}
                                    />
                                ),
                            }}
                        >
                            {renderDayPicker('to')}
                        </Dropdown>
                    </GridColumn>
                </>
            }
        </Grid>;
    };

    return (
        <>
            {isMounted && (props.type === 'range' ? renderRangeFields(props as IDatepickerRangeProps) : renderSingleField(props as IDatepickerSingleProps))}
        </>
    );
};

export const isRange: (date: Date | DateRange | undefined) => boolean = (date: Date | DateRange | undefined): date is DateRange =>
    (date as DateRange).from !== undefined || (date as DateRange).to !== undefined;


export default Datepicker;
